/**
 * VotingModal Component
 *
 * Modal for employee of the month voting
 * - Shows all employees (except current user)
 * - Allows vote submission with optional reason
 * - Shows voting results if already voted
 * - Displays voting period information
 */

import { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Trophy, CheckCircle, Users, Clock, Award } from 'lucide-react';
import { VotingPeriod } from '@/types';
import { useTeamMembers } from '@/hooks/useVoting';
import { useVoting } from '@/hooks/useVoting';
import type { User } from '@/utils/seedData';
import { getLocaleSync } from '@/utils/locale';

interface VotingModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string | null;
  userName: string;
  votingPeriod: VotingPeriod | null;
  hasVoted: boolean;
  tenantId?: string;
}

export default function VotingModal({
  isOpen,
  onClose,
  userId,
  userName,
  votingPeriod,
  hasVoted,
  tenantId,
}: VotingModalProps) {
  const { teamMembers, loading: loadingTeam } = useTeamMembers(tenantId);
  const { submitVote, userVote, daysRemaining, hoursRemaining } = useVoting(
    userId,
    userName,
    tenantId
  );

  const [selectedNomineeId, setSelectedNomineeId] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  if (!isOpen || !votingPeriod) return null;

  // Filter out current user and deduplicate by name
  const eligibleNominees = teamMembers
    .filter((member: User) => member.id !== userId)
    .reduce((acc: User[], member: User) => {
      // Check if we already have someone with this name
      const existingMember = acc.find((m: User) => m.name === member.name);

      if (!existingMember) {
        acc.push(member);
      } else {
        // If duplicate exists, prefer the one without "-tech" suffix
        if (member.id!.includes('-tech') && !existingMember.id!.includes('-tech')) {
          // Keep existing (non-tech version)
          return acc;
        } else if (!member.id!.includes('-tech') && existingMember.id!.includes('-tech')) {
          // Replace with non-tech version
          const index = acc.indexOf(existingMember);
          acc[index] = member;
        }
      }

      return acc;
    }, []);

  // Get selected nominee details
  const selectedNominee = eligibleNominees.find(
    (member: User) => member.id === selectedNomineeId
  );

  // Format date
  const formatDate = (isoString: string) => {
    return new Date(isoString).toLocaleDateString(getLocaleSync(), {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Handle vote submission
  const handleSubmit = async () => {
    if (!selectedNomineeId || !selectedNominee) {
      setError('Please select a team member to nominate');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await submitVote(selectedNomineeId, selectedNominee.name, reason || undefined);
      setSuccess(true);
      setTimeout(() => {
        onClose();
        // Reset form
        setSelectedNomineeId(null);
        setReason('');
        setSuccess(false);
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit vote');
      setSubmitting(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[200] flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500 to-yellow-500 p-6 text-white relative overflow-hidden">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="flex items-center gap-3 mb-2">
            <Trophy className="w-8 h-8" />
            <h2 className="text-2xl font-bold">{votingPeriod.title}</h2>
          </div>

          <div className="flex items-center gap-4 text-sm opacity-90">
            <div className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              <span>
                {formatDate(votingPeriod.startDate)} - {formatDate(votingPeriod.endDate)}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <Award className="w-4 h-4" />
              <span>
                {daysRemaining > 0
                  ? `${daysRemaining} day${daysRemaining === 1 ? '' : 's'} remaining`
                  : `${hoursRemaining} hour${hoursRemaining === 1 ? '' : 's'} remaining`}
              </span>
            </div>
          </div>

          {/* Decorative elements */}
          <div className="absolute -right-8 -bottom-8 opacity-10">
            <Trophy className="w-32 h-32" />
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Already Voted State */}
          {hasVoted && userVote ? (
            <div className="text-center py-8">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full mb-4">
                <CheckCircle className="w-12 h-12 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Thank You for Voting!
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                You voted for <span className="font-semibold">{userVote.nomineeName}</span>
              </p>
              {userVote.reason && (
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 max-w-md mx-auto">
                  <p className="text-sm text-gray-600 dark:text-gray-400 italic">
                    "{userVote.reason}"
                  </p>
                </div>
              )}
              <p className="text-sm text-gray-500 dark:text-gray-500 mt-4">
                Results will be announced after voting closes
              </p>
            </div>
          ) : (
            <>
              {/* Voting Form */}
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-4">
                  <Users className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Select a Team Member
                  </h3>
                </div>

                {loadingTeam ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-60 overflow-y-auto">
                    {eligibleNominees.map((member: User) => (
                      <button
                        key={member.id}
                        onClick={() => setSelectedNomineeId(member.id!)}
                        className={`
                          p-4 rounded-lg border-2 transition-all text-left
                          ${
                            selectedNomineeId === member.id
                              ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20'
                              : 'border-gray-200 dark:border-gray-700 hover:border-amber-300'
                          }
                        `}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-semibold text-gray-900 dark:text-white">
                              {member.name}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400 capitalize">
                              {member.role}
                            </div>
                          </div>
                          {selectedNomineeId === member.id && (
                            <CheckCircle className="w-5 h-5 text-amber-500" />
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Optional Reason */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Why are they deserving? (Optional)
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Share why this person deserves recognition..."
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                           bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                           focus:ring-2 focus:ring-amber-500 focus:border-transparent
                           placeholder-gray-400 dark:placeholder-gray-500"
                  maxLength={500}
                />
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {reason.length}/500 characters
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
                  {error}
                </div>
              )}

              {/* Success Message */}
              {success && (
                <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-400 text-sm flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  Vote submitted successfully!
                </div>
              )}

              {/* Submit Button */}
              <button
                onClick={handleSubmit}
                disabled={!selectedNomineeId || submitting}
                className={`
                  w-full py-3 px-6 rounded-lg font-semibold text-white
                  transition-all duration-200
                  ${
                    !selectedNomineeId || submitting
                      ? 'bg-gray-300 dark:bg-gray-700 cursor-not-allowed'
                      : 'bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 shadow-lg hover:shadow-xl transform hover:scale-[1.02]'
                  }
                `}
              >
                {submitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Submitting Vote...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <Trophy className="w-5 h-5" />
                    Submit Vote
                  </span>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
